/**
 * Formulario de un pedido de láminas (alta única + edición), con campos
 * condicionales por categoría.
 *
 * Lo orquestan dos pantallas: el modal "Nuevo pedido" del tablero
 * (`PedidoFormModal`, modo `create`) y la pantalla de edición
 * (`EditarPedidoScreen`, modo `edit`). Responsabilidades de ESTE componente
 * (presentación + validación local):
 *  - Estado controlado de los campos.
 *  - Campos condicionales por categoría (igual que el SSR/service): calibre si la
 *    categoría lo lleva; medida si la lleva; tubo si es "Tubos"; descripción
 *    siempre visible (obligatoria si no hay medida estándar).
 *  - Validación con Zod (`pedidoFormSchema`) antes de delegar: si falla, muestra
 *    los errores por campo y no llama a `onSubmit`.
 *  - Mensaje de error de la API anclado al formulario.
 *
 * No hace `fetch` ni decide permisos: recibe los valores iniciales, el estado de
 * envío (`isSubmitting`) y el error de la API (`apiError`) desde la pantalla, que
 * posee los hooks de datos. En `edit`, las sedes origen/destino no se editan (son
 * fijas 39<->41): se muestran solo-lectura. Usa `components/ui`.
 */
import { useState } from "react";

import { Button, Input } from "@/components/ui";

import {
  PEDIDOS_CALIBRES,
  PEDIDOS_CATEGORIAS,
  PEDIDOS_MEDIDAS,
  PEDIDOS_TUBOS,
  categoriaEsTubo,
  categoriaTieneCalibre,
  categoriaTieneMedida,
} from "../constants";
import { pedidoErrorMessage } from "../format";
import { pedidoFormSchema, type PedidoFormValues } from "../schemas";

type FieldKey = keyof PedidoFormValues;
type FieldErrors = Partial<Record<FieldKey, string>>;

interface PedidoFormProps {
  initialValues: PedidoFormValues;
  /** Nombre de la sede origen/destino para mostrar (solo informativo). */
  origenNombre?: string | null;
  destinoNombre?: string | null;
  isSubmitting: boolean;
  apiError: unknown;
  onSubmit: (values: PedidoFormValues) => void;
  submitLabel: string;
  onCancel: () => void;
}

/** Estilo de un `<select>` coherente con `components/ui/Input`. */
const SELECT_CLASS =
  "block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-red-500";

export function PedidoForm({
  initialValues,
  origenNombre,
  destinoNombre,
  isSubmitting,
  apiError,
  onSubmit,
  submitLabel,
  onCancel,
}: PedidoFormProps) {
  const [values, setValues] = useState<PedidoFormValues>(initialValues);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const formApiError = apiError ? pedidoErrorMessage(apiError) : null;
  const categoria = values.categoria.trim();
  const showCalibre = categoriaTieneCalibre(categoria);
  const showMedida = categoriaTieneMedida(categoria);
  const showTubo = categoriaEsTubo(categoria);
  const calibresDeCategoria = PEDIDOS_CALIBRES[categoria] ?? [];

  function setValue<K extends FieldKey>(field: K, value: PedidoFormValues[K]) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  /** Al cambiar la categoría, limpia los campos que ya no aplican. */
  function changeCategoria(next: string) {
    setValues((prev) => ({
      ...prev,
      categoria: next,
      calibre: categoriaTieneCalibre(next) ? prev.calibre : "",
      medida: categoriaTieneMedida(next) ? prev.medida : "",
      tubo: categoriaEsTubo(next) ? prev.tubo : "",
    }));
  }

  function errorFor(field: FieldKey): string | undefined {
    return fieldErrors[field];
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const result = pedidoFormSchema.safeParse(values);
    if (!result.success) {
      const flattened = result.error.flatten().fieldErrors;
      const errors: FieldErrors = {};
      for (const [key, messages] of Object.entries(flattened)) {
        const first = messages?.[0];
        if (first) {
          errors[key as FieldKey] = first;
        }
      }
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    onSubmit(result.data);
  }

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit} noValidate>
      {/* Categoría */}
      <div className="flex flex-col gap-1">
        <label htmlFor="pl-categoria" className="text-sm font-medium text-slate-700">
          Categoría *
        </label>
        <select
          id="pl-categoria"
          className={SELECT_CLASS}
          value={values.categoria}
          aria-invalid={errorFor("categoria") ? true : undefined}
          onChange={(event) => changeCategoria(event.target.value)}
        >
          <option value="">Seleccione una categoría</option>
          {PEDIDOS_CATEGORIAS.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <FieldError message={errorFor("categoria")} />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {/* Calibre (condicional) */}
        {showCalibre && (
          <div className="flex flex-col gap-1">
            <label htmlFor="pl-calibre" className="text-sm font-medium text-slate-700">
              Calibre *
            </label>
            <select
              id="pl-calibre"
              className={SELECT_CLASS}
              value={values.calibre ?? ""}
              aria-invalid={errorFor("calibre") ? true : undefined}
              onChange={(event) => setValue("calibre", event.target.value)}
            >
              <option value="">Seleccione un calibre</option>
              {calibresDeCategoria.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <FieldError message={errorFor("calibre")} />
          </div>
        )}

        {/* Medida (condicional) */}
        {showMedida && (
          <div className="flex flex-col gap-1">
            <label htmlFor="pl-medida" className="text-sm font-medium text-slate-700">
              Medida *
            </label>
            <select
              id="pl-medida"
              className={SELECT_CLASS}
              value={values.medida ?? ""}
              aria-invalid={errorFor("medida") ? true : undefined}
              onChange={(event) => setValue("medida", event.target.value)}
            >
              <option value="">Seleccione una medida</option>
              {PEDIDOS_MEDIDAS.map((m) => (
                <option key={m} value={m}>
                  {m === "otra" ? "Otra (describir)" : m}
                </option>
              ))}
            </select>
            <FieldError message={errorFor("medida")} />
          </div>
        )}
      </div>

      {/* Tubo (condicional) */}
      {showTubo && (
        <div className="flex flex-col gap-1">
          <label htmlFor="pl-tubo" className="text-sm font-medium text-slate-700">
            Medida de tubo *
          </label>
          <select
            id="pl-tubo"
            className={SELECT_CLASS}
            value={values.tubo ?? ""}
            aria-invalid={errorFor("tubo") ? true : undefined}
            onChange={(event) => setValue("tubo", event.target.value)}
          >
            <option value="">Seleccione una medida</option>
            {PEDIDOS_TUBOS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <FieldError message={errorFor("tubo")} />
        </div>
      )}

      {/* Descripción */}
      <div className="flex flex-col gap-1">
        <label htmlFor="pl-descripcion" className="text-sm font-medium text-slate-700">
          Descripción del producto
        </label>
        <textarea
          id="pl-descripcion"
          className="block min-h-20 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand aria-[invalid=true]:border-red-500"
          value={values.descripcion ?? ""}
          aria-invalid={errorFor("descripcion") ? true : undefined}
          placeholder="Describe el material con más detalle…"
          onChange={(event) => setValue("descripcion", event.target.value)}
        />
        <FieldError message={errorFor("descripcion")} />
      </div>

      {/* Cantidad */}
      <div className="flex flex-col gap-1">
        <label htmlFor="pl-cantidad" className="text-sm font-medium text-slate-700">
          Cantidad *
        </label>
        <Input
          id="pl-cantidad"
          type="number"
          min={1}
          inputMode="numeric"
          value={values.cantidad}
          aria-invalid={errorFor("cantidad") ? true : undefined}
          onChange={(event) => setValue("cantidad", event.target.value)}
        />
        <FieldError message={errorFor("cantidad")} />
      </div>

      {/* Sedes (informativo, no editable: son fijas 39<->41) */}
      {(origenNombre || destinoNombre) && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700">
          <span className="rounded-md bg-amber-100 px-2 py-1 font-semibold text-amber-800">
            {origenNombre ?? "Origen"}
          </span>
          <span className="text-slate-400">→</span>
          <span className="rounded-md bg-blue-100 px-2 py-1 font-semibold text-blue-800">
            {destinoNombre ?? "Destino"}
          </span>
        </div>
      )}

      {formApiError && (
        <p role="alert" className="text-sm text-red-600">
          {formApiError}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" size="md" disabled={isSubmitting}>
          {isSubmitting ? "Guardando…" : submitLabel}
        </Button>
        <Button type="button" variant="secondary" size="md" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/** Texto de error bajo un campo (o nada). */
function FieldError({ message }: { message?: string | undefined }) {
  if (!message) {
    return null;
  }
  return <p className="text-sm text-red-600">{message}</p>;
}
